/**
 * Entry point for development and production PWA builds.
 */
import 'regenerator-runtime/runtime';
import App from './App';
import React from 'react';
import ReactDOM from 'react-dom';
import { PatientProvider } from '@ohif/ui';

/**
 * EXTENSIONS AND MODES
 * =================
 * pluginImports.js is dynamically generated from extension and mode
 * configuration at build time.
 *
 * pluginImports.js imports all of the modes and extensions and adds them
 * to the window for processing.
 */
import { modes as defaultModes, extensions as defaultExtensions } from './pluginImports';
import loadDynamicConfig from './loadDynamicConfig';

const FORCE_CLEAN_QUERY_PARAM = 'forceCleanAuth';
const CLEANUP_LOG_PREFIX = '[OHIF-BOOT-CLEANUP]';
const CLEANUP_LOG_LEVEL = process.env.NODE_ENV === 'production' ? 'info' : 'debug';

function cleanupLog(eventName, payload = {}) {
  const logger = CLEANUP_LOG_LEVEL === 'debug' ? console.debug : console.info;
  logger(CLEANUP_LOG_PREFIX, eventName, payload);
}

function cleanupWarn(message, error) {
  console.warn(CLEANUP_LOG_PREFIX, message, error);
}

function isTruthyQueryValue(value) {
  return value === '1' || value === 'true' || value === 'yes';
}

async function forceClientCleanup() {
  try {
    localStorage.clear();
    sessionStorage.clear();
  } catch (error) {
    cleanupWarn('Could not clear browser storage during forced cleanup', error);
  }

  if ('serviceWorker' in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(registration => registration.unregister()));
    } catch (error) {
      cleanupWarn('Could not unregister service workers during forced cleanup', error);
    }
  }

  if ('caches' in window) {
    try {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(cacheName => caches.delete(cacheName)));
    } catch (error) {
      cleanupWarn('Could not clear Cache Storage during forced cleanup', error);
    }
  }
}

async function bootstrap() {
  const bootstrapStartedAt = Date.now();
  const cleanupPromise = window.__OHIF_BOOT_CLEANUP_PROMISE__;
  if (cleanupPromise?.then) {
    cleanupLog('waiting_head_cleanup_promise');
    await cleanupPromise;
    cleanupLog('head_cleanup_promise_resolved');
  }

  const query = new URLSearchParams(window.location.search);
  const forceCleanAuthParam = query.get(FORCE_CLEAN_QUERY_PARAM)?.toLowerCase();
  const forceCleanAuthFromQuery = isTruthyQueryValue(forceCleanAuthParam);
  const forceCleanByToken = Boolean(window.__OHIF_HAS_INCOMING_TOKEN__);
  const forceCleanAuth = forceCleanAuthFromQuery || forceCleanByToken;

  cleanupLog('bootstrap_mode', {
    forceCleanByToken,
    forceCleanAuthFromQuery,
    forceCleanAuth,
    alreadyCleanedInHead: Boolean(window.__OHIF_BOOT_FORCE_CLEANED__),
    logLevel: CLEANUP_LOG_LEVEL,
  });

  if (forceCleanAuth && !window.__OHIF_BOOT_FORCE_CLEANED__) {
    cleanupLog('running_fallback_cleanup_in_index');
    await forceClientCleanup();

    // Remove the cleanup param after a successful run to avoid cleaning again on refresh.
    query.delete(FORCE_CLEAN_QUERY_PARAM);
    const remainingQueryString = query.toString();
    const queryPrefix = remainingQueryString ? '?' : '';
    const cleanUrl =
      window.location.pathname + queryPrefix + remainingQueryString + window.location.hash;
    window.history.replaceState({}, document.title, cleanUrl);
  }

  const config_json = await loadDynamicConfig(window.config, { disableCache: forceCleanAuth });

  cleanupLog('config_loaded', {
    cacheBypassEnabled: forceCleanAuth,
  });

  // Reset Dynamic config if defined
  if (config_json !== null) {
    window.config = config_json;
  }

  /**
   * Combine our appConfiguration with installed extensions and modes.
   * In the future appConfiguration may contain modes added at runtime.
   *  */
  const appProps = {
    config: window ? window.config : {},
    defaultExtensions,
    defaultModes,
  };

  /** Create App */
  const app = React.createElement(PatientProvider, null, React.createElement(App, appProps, null));
  /** Render */
  ReactDOM.render(app, document.getElementById('root'));

  cleanupLog('react_mounted', {
    durationMs: Date.now() - bootstrapStartedAt,
  });
}

bootstrap();

export { history } from './utils/history';
